import { call, select, put } from 'redux-saga/effects';

import UserClient from '../../api/UserClient';
import { invoicesReplayDataSelector, invoicesReplayProviderSelector } from '../../selectors/invoicesReplay';
import { setupStripeIntentSuccessful } from '../../redux/app/actions';
import { pspPublishableKeySelector } from '../../selectors/app';
import {
    setDisplayRedirectToInvoices,
    setInvoicesReplayProvider,
    setIsPaymentProcessing,
    setReplayAdyenSession,
    getInvoicesReplayData,
} from '../../redux/invoicesReplay/actions';
import { messagesShow } from '../../redux/messages/actions';
import { USER_SET_FAILED_INVOICES_PROCESSING } from '../../redux/user/types';

// PaymentIntentDTO.status values that leave nothing for the shopper to do.
const NO_SHOPPER_ACTION_STATUSES = ['SUCCEEDED', 'REQUIRES_CAPTURE', 'PROCESSING'];
const DEAD_REPLAY_STATUSES = ['CANCELLED', 'PROCESSING_CANCELLATION', 'FAILED_CANCELLATION'];

const normalizeProvider = pspName => {
    const name = String(pspName || '').toUpperCase();
    return name === 'ADYEN' || name === 'STRIPE' ? name : null;
};

export function* payInvoicesSaga() {
    yield put(setIsPaymentProcessing(true));
    const data = yield select(invoicesReplayDataSelector);
    try {
        const response = yield call(UserClient.onPayInvoices, data);
        const { status, pspClientSecret, pspPublishableKey, paymentOption } = response;
        if (status === 'FAILED') {
            throw new Error('Replay payment failed. Please try again.');
        }

        // This response is per-transaction, so it outranks the value /paymentOptions
        // left in the store. Reconciled rather than or-ed, so the screen reading the
        // store cannot end up on a different branch than this saga.
        const storedProvider = yield select(invoicesReplayProviderSelector);
        const provider = normalizeProvider(response.pspName) || storedProvider;
        if (provider !== storedProvider) {
            yield put(setInvoicesReplayProvider(provider));
        }

        // Adyen replays must never reach the Stripe branch: pspClientSecret holds an
        // Adyen sessionData, which makes the Stripe SDK throw an IntegrationError.
        if (provider === 'ADYEN') {
            if (NO_SHOPPER_ACTION_STATUSES.includes(status)) {
                // Released here, not left to the refresh: its failure path does not
                // clear the flag and the CTA would stay disabled until a reload.
                yield put(setIsPaymentProcessing(false));
                yield put(getInvoicesReplayData());
                return;
            }
            // The BE returns the PG intent DTO; paymentOption.adyenSession is the
            // shape documented on ISJ-9345.
            const session = (paymentOption && paymentOption.adyenSession) || {};
            const id = session.sessionId || response.pspReference;
            const sessionData = session.sessionData || pspClientSecret;
            const isDead = DEAD_REPLAY_STATUSES.includes(status);
            if (isDead || !id || !sessionData) {
                yield put(messagesShow('PaymentError', 'error'));
                yield put(setIsPaymentProcessing(false));
                return;
            }
            // An incomplete session makes AdyenCheckout throw "No clientKey available".
            const clientKey = pspPublishableKey || (yield select(pspPublishableKeySelector));
            if (!clientKey) {
                yield put(messagesShow('PaymentError', 'error'));
                yield put(setIsPaymentProcessing(false));
                return;
            }
            yield put(setReplayAdyenSession({ id, sessionData, clientKey }));
            yield put(setDisplayRedirectToInvoices(false));
            return;
        }

        yield put(setupStripeIntentSuccessful({ clientSecret: pspClientSecret, pubKey: pspPublishableKey }));
        yield put(setDisplayRedirectToInvoices(false));
    } catch (error) {
        console.error('Error in payInvoicesSaga:', error);
        const errorMsg = error.message && typeof error.message === 'string' ? error.message : 'internalServerError';
        yield put(messagesShow(errorMsg, 'error'));
        // Re-enable the CTA so an abandoned or expired 3DS can be retried.
        yield put(setIsPaymentProcessing(false));
    }
}

export function* payFailedInvoicesSaga() {
    try {
        yield call(UserClient.onPayFailedInvoices);
        yield put({ type: USER_SET_FAILED_INVOICES_PROCESSING });
        yield put(messagesShow('InvoicesProcessingOKMessage', 'success'));
    } catch (error) {
        yield put(messagesShow(error, 'error'));
    }
}
